import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Mail, Building2, Calendar, Users } from "lucide-react";
import { apiFetch } from "@/lib/api-fetch";

type WaitlistEntry = {
  id: number;
  email: string;
  name: string | null;
  company: string | null;
  source: string | null;
  created_at: string;
};

export default function WaitlistPage() {
  const { token } = useAuth();
  const [entries, setEntries] = useState<WaitlistEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch("/api/admin/waitlist", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => setEntries(data.entries || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [token]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Waitlist</h1>
          <p className="text-sm text-muted-foreground mt-1">
            People who signed up for early access
          </p>
        </div>
        <div className="flex items-center gap-2 px-3 py-2 bg-card border border-border rounded-lg">
          <Users size={16} className="text-purple-500" />
          <span className="text-sm font-semibold text-foreground">{entries.length}</span>
          <span className="text-xs text-muted-foreground">total</span>
        </div>
      </div>

      {loading ? (
        <div className="bg-card border border-border rounded-xl p-8">
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-12 bg-muted rounded-lg animate-pulse" />
            ))}
          </div>
        </div>
      ) : entries.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center">
          <div className="w-12 h-12 mx-auto rounded-xl bg-secondary flex items-center justify-center mb-3">
            <Users size={24} className="text-muted-foreground" />
          </div>
          <p className="text-muted-foreground">No waitlist signups yet.</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">#</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Email</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Name</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Company</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Source</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Signed Up</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {entries.map((entry, i) => (
                  <tr key={entry.id} className="hover:bg-secondary/50 transition-colors">
                    <td className="px-4 py-3 text-xs text-muted-foreground">{i + 1}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <Mail size={14} className="text-muted-foreground" />
                        <a
                          href={`mailto:${entry.email}`}
                          className="text-sm text-foreground hover:text-forest-500"
                        >
                          {entry.email}
                        </a>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">
                      {entry.name || <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      {entry.company ? (
                        <div className="flex items-center gap-1.5">
                          <Building2 size={14} className="text-muted-foreground" />
                          <span className="text-sm text-foreground">{entry.company}</span>
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-secondary text-muted-foreground">
                        {entry.source || "website"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Calendar size={12} />
                        {new Date(entry.created_at).toLocaleDateString()}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
